<template>
  <v-dialog v-model="dialog" persistent max-width="500px">
    <template #activator="{ on }">
      <v-text-field :value="textValue" label="Category" readonly data-cy="category" v-on="on" />
    </template>
    <nv-form
      title="Select Subcategory"
      data-cy="category-form"
      @cancel="closeDialog"
      @nv-submit="save"
    >
      <template v-if="canView()" #title-extend>
        <v-spacer />
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              medium
              class="ai-button"
              :loading="aiLoading"
              :disabled="!entityName"
              v-bind="attrs"
              v-on="on"
              @click="generateCategoryWithMav"
            >
              <v-icon color="white">mdi-robot</v-icon>
            </v-btn>
          </template>
          <span>Ask MAV</span>
        </v-tooltip>
      </template>
      <v-row>
        <v-col cols="12">
          <v-select
            v-model="selectedCategory"
            :items="categories"
            :rules="rules.category"
            item-text="name"
            item-value="id"
            label="Category"
            data-cy="categories"
            @change="fetchSubcategories"
          />
        </v-col>
        <v-col cols="12">
          <v-select
            v-model="item"
            :items="subcategories"
            :disabled="!selectedCategory"
            :rules="rules.subcategory"
            :loading="loading"
            validate-on-blur
            item-text="name"
            return-object
            label="Subcategory"
            data-cy="sub-categories"
          />
        </v-col>
      </v-row>
    </nv-form>
  </v-dialog>
</template>

<script>
import HTTP from '@/api/owl';
import MAV from '@/api/mav';
import Rules from 'bsm-validation-rules';
import { getConfigMap } from '@/helpers/filters';
import { submitFeedback } from '@/api/feedback';

const rules = {
  category: [Rules.presence()],
  subcategory: [Rules.presence()],
};

export default {
  props: {
    value: {
      type: Number,
      default: null,
    },
    editing: {
      type: Object,
      default: null,
    },
    entityName: {
      type: String,
      default: '',
    },
    context: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    categories: [],
    subcategories: [],
    dialog: false,
    item: {},
    selectedCategory: null,
    textValue: '',
    loading: false,
    aiLoading: false,
    aiSuggestionFull: null,
    rules,
  }),
  watch: {
    editing(val) {
      if (val) {
        this.setInitial();
      } else {
        // Reset to empty state when editing is null/undefined
        this.selectedCategory = null;
        this.item = {};
        this.textValue = '';
      }
    },
    dialog(val) {
      if (val && this.editing && this.value !== this.item.id) {
        this.setInitial();
      }
    },
  },
  created() {
    this.fetchCategories();
  },
  methods: {
    save(form) {
      if (form.validate()) {
        this.setTextValue();

        if (!this.isShadowMode && this.aiSuggestionFull && this.item.id) {
          if (this.isProductionMode()) {
            this.sendFeedback();
          }
        }

        if (this.isShadowMode) {
          try {
            this.sendShadowEvaluation(
              this.entityName,
              this.getParentName(),
              this.item.category?.id || this.item.category_id || this.item.category,
              this.item.id,
              this.getLocale(),
              this.getAdSupported(),
              this.getStatus(),
            );
          } catch (error) {
            console.log(error);
          }
        }

        this.$emit('input', this.item.id);
        this.dialog = false;
      }
    },
    getParentName() {
      return (
        this.context?.parent_name ||
        this.context?.brand_name ||
        this.context?.parent?.name ||
        this.context?.brand?.name ||
        ''
      );
    },
    getLocale() {
      return this.context?.country_code || this.context?.locale || 'us';
    },
    getAdSupported() {
      return this.context?.ad_supported || 'Unknown';
    },
    getStatus() {
      return this.context?.status || 'Active';
    },
    async sendFeedback() {
      try {
        const userCategoryId =
          this.item.category?.id || this.item.category_id || this.item.category;
        const userSubCategoryId = this.item.id;

        const action =
          this.aiSuggestionFull.category_id == userCategoryId &&
          this.aiSuggestionFull.sub_category_id == userSubCategoryId
            ? 'ACCEPTED'
            : 'CORRECTED';

        await submitFeedback({
          input_string: this.entityName,
          ai_suggestion: {
            category_id: this.aiSuggestionFull.category_id,
            sub_category_id: this.aiSuggestionFull.sub_category_id,
            rationale: this.aiSuggestionFull.rationale || '',
            confidence: this.aiSuggestionFull.confidence || 0,
            entity_type: 'category',
          },
          user_final_decision: {
            category_id: userCategoryId,
            sub_category_id: userSubCategoryId,
            entity_type: 'category',
          },
          action,
        });
      } catch (error) {
        console.error('Failed to submit feedback:', error);
      }
    },
    fetchCategories() {
      HTTP.get('/categories').then(({ data }) => {
        this.categories = data;
      });
    },
    fetchSubcategories() {
      this.item = {};
      this.loading = true;
      return HTTP.get(`/categories/${this.selectedCategory}/sub_categories`)
        .then(({ data }) => {
          this.subcategories = data;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    setTextValue() {
      this.textValue = `${this.item.category.name} / ${this.item.name}`;
    },
    closeDialog(form) {
      this.dialog = false;
      if (form != null) {
        form.resetValidation();
      }
    },
    async setInitial() {
      // Handle empty or null editing values
      if (!this.editing || !this.editing.category || !this.editing.category.id) {
        this.selectedCategory = null;
        this.item = {};
        this.textValue = '';
        return;
      }

      this.selectedCategory = this.editing?.category?.id;
      await this.fetchSubcategories();
      this.item = { ...this.editing };
      this.setTextValue();
    },
    async generateCategoryWithMav() {
      if (!this.entityName) {
        this.$flash('warning', 'Please enter a name first');
        return;
      }

      this.aiLoading = true;
      try {
        const parent = this.getParentName();
        const locale = this.getLocale();
        const adSupported = this.getAdSupported();
        const status = this.getStatus();

        const response = await this.callMavAPI(
          this.entityName,
          parent,
          locale,
          adSupported,
          status,
        );

        if (response.category_id && response.sub_category_id) {
          this.aiSuggestionFull = response;
          this.selectedCategory = response.category_id;

          await this.fetchSubcategories();

          const subcategory = this.subcategories.find(sub => sub.id == response.sub_category_id);

          if (subcategory) {
            this.selectedCategory = subcategory.category;
            this.item = subcategory;
            this.$flash('success', 'Category suggestion applied');
          }
        }
      } catch (error) {
        console.error('MAV AI category prediction failed:', error);
        this.$flash('error', 'Failed to predict category with MAV AI');
      } finally {
        this.aiLoading = false;
      }
    },
    async callMavAPI(
      entity,
      parent,
      locale = 'us',
      adSupported = 'Unknown',
      status = 'Active',
      expectedCategoryId = null,
      expectedSubCategoryId = null,
    ) {
      try {
        if (this.isShadowMode() && expectedCategoryId && expectedSubCategoryId) {
          const response = await MAV.post('/shadow/categories/predict', {
            entity,
            parent: parent || '',
            locale,
            ad_supported: adSupported,
            status,
            expected_category_id: expectedCategoryId,
            expected_sub_category_id: expectedSubCategoryId,
          });
          return response.data.ai_suggestion || response.data;
        }

        const response = await MAV.post('/categories/predict', {
          entity,
          parent: parent || '',
          locale,
          ad_supported: adSupported,
          status,
        });
        return response.data;
      } catch (error) {
        console.error('MAV API error:', error);
        throw error;
      }
    },
    async sendShadowEvaluation(
      entity,
      parent,
      expectedCategoryId,
      expectedSubCategoryId,
      locale = 'us',
      adSupported = 'Unknown',
      status = 'Active',
    ) {
      try {
        await MAV.post('/shadow/categories/predict', {
          entity,
          parent: parent || '',
          locale,
          ad_supported: adSupported,
          status,
          expected_category_id: expectedCategoryId,
          expected_sub_category_id: expectedSubCategoryId,
        });
      } catch (error) {
        console.error('Shadow API error:', error);
      }
    },
    canView() {
      const config = this.getConfigMap();
      const subcatConfig = config['mavFeature_owl_ui']?.subcat_enabled;
      return subcatConfig?.v1 || subcatConfig?.v2 || false;
    },
    isShadowMode() {
      const config = this.getConfigMap();
      const subcatConfig = config['mavFeature_owl_ui']?.subcat_enabled;
      return subcatConfig?.shadow === true && !subcatConfig?.v1 && !subcatConfig?.v2;
    },
    isProductionMode() {
      const config = this.getConfigMap();
      const subcatConfig = config['mavFeature_owl_ui']?.subcat_enabled;
      return (subcatConfig?.v1 || subcatConfig?.v2) && !subcatConfig?.shadow;
    },
    getConfigMap,
  },
};
</script>

<style scoped>
.ai-button {
  left: 8px;
  right: 8pc;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3) !important;
  transition: all 0.3s ease !important;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important;
    box-shadow: 0 6px 12px rgba(102, 126, 234, 0.5) !important;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
  }
}
</style>
